import * as React from 'react';
// Import material ui modules
import {
	AddCircleOutlineOutlined,
} from '@mui/icons-material';
// Import props.
import {
    ComparativeDataHeaderProps,
	ComparativeMortgageesSummaryProps,
	ComparativeMortgageeSummaryProps,
	IconButtonProps,
} from '@app/props';
// Import mui components.
import {
	AccountBalanceOutlined,
} from '@mui/icons-material';

interface UseComparativeMortgageesSummaryParameters {
	props: ComparativeMortgageesSummaryProps;
}
interface UseComparativeMortgageesSummaryResults {
	applyComparativeMortgageeSummaryPropsList: () => ComparativeMortgageeSummaryProps[];
	applyComparativeMortgageesSummaryHeaderProps: () => ComparativeDataHeaderProps;
}

const useComparativeMortgageesSummary = (params: UseComparativeMortgageesSummaryParameters): UseComparativeMortgageesSummaryResults => {

	// #region exported methods

	const applyComparativeMortgageeSummaryPropsList = (): ComparativeMortgageeSummaryProps[] => {
		return params.props.comparisonMortgageesSummary.comparisonEntitySummaries.map((comparisonMortgageeSummary, index: number) => {
			return {
				id: generateIdForComparisonMortgageeSummary(comparisonMortgageeSummary.id),
				defaultTitle: `Mortgagee ${index + 1}`,
				comparisonEntitySummary: comparisonMortgageeSummary,
				focusToErrorOnMount: focusToAnyErrorOnMount
					&& firstMortgageeGuidThatRequiresReview !== undefined
					&& firstMortgageeGuidThatRequiresReview === comparisonMortgageeSummary.id,
				possibleStates: params.props.possibleStates,
				entityActionProps: params.props.entityActionProps,
				isHeaderSticky: areHeadersSticky,
			}
		});
	}

	const applyComparativeMortgageesSummaryHeaderProps = (): ComparativeDataHeaderProps => {
		return {
			id: generateIdForComparisonMortgageesSummaryHeader(),
			isSticky: areHeadersSticky,
			title: 'Mortgagees',
			titleIcon: AccountBalanceOutlined,
			titleFontSize: 'md',
			iconButtons: applyIconButtonPropsList(),
			spacingBetweenIconAndTitle: 1,
			paddingProps: {
				bottom: 0,
				top: 1,
			}
		};
	}

	// #endregion exported methods


	// #region private methods

	const applyIconButtonPropsList = (): IconButtonProps[] => {
		let iconButtonsProps: IconButtonProps[] = [];

		if (params.props.comparisonMortgageesSummary.allowAdds) {
			iconButtonsProps = [
				{
					icon: AddCircleOutlineOutlined,
					label: 'Add Mortgagee',
					onClick: params.props.entityActionProps.onAddEntity,
					ref: addButtonRef,
				}
			];
		}

		return iconButtonsProps;
	}

	const generateIdForComparisonMortgageeSummary = (comparisonMortgageeSummaryId: string): string => {
		return `${params.props.id}-mortgageeSummary-${comparisonMortgageeSummaryId}`;
	}

	const generateIdForComparisonMortgageesSummaryHeader = (): string => {
		return `${params.props.id}-mortgageesSummary-header`;
	}

	// #endregion private methods


	// #region variables

	const areHeadersSticky: boolean = React.useMemo(() => {
		return params.props.areHeadersSticky ?? false;
	}, [params.props.areHeadersSticky]);

	// Focus to the edit button of any mortgagee that has an error.
	const focusToAnyErrorOnMount: boolean = React.useMemo(
		() => params.props.focusToAnyErrorOnMount ?? false,
		[params.props.focusToAnyErrorOnMount],
	);
	// Focus to the add button if prompted to, and there are no other mortgagees requiring review.
	const focusToTopOnMount: boolean = React.useMemo(
		() => (params.props.focusToTopOnMount ?? false) && !focusToAnyErrorOnMount,
		[params.props.focusToTopOnMount, focusToAnyErrorOnMount],
	);
	// If there are any mortgagees with errors, then focus to the first id one on the list.
	const firstMortgageeGuidThatRequiresReview: string | undefined = React.useMemo(
		() => params.props.comparisonMortgageesSummary.comparisonEntitySummaries.find(comparisonMortgageeSummary => comparisonMortgageeSummary.requiresReview)?.id,
		[params.props.comparisonMortgageesSummary]
	);

	const addButtonRef: React.RefObject<HTMLButtonElement> = React.useRef<HTMLButtonElement>(null);

	// #endregion private variables

	// #region life-cycle methods

	React.useEffect(() => {
		if (focusToTopOnMount
			&& addButtonRef.current !== null) {
			addButtonRef.current.focus();
		}
	}, []);

	// #endregion life-cycle methods.

	return {
		applyComparativeMortgageeSummaryPropsList,
		applyComparativeMortgageesSummaryHeaderProps,
	};
}

// Export parameters.
export {
	UseComparativeMortgageesSummaryParameters,
}

// Export results.
export {
	UseComparativeMortgageesSummaryResults,
}

export default useComparativeMortgageesSummary;